import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, FileText, Building2, MapPin, Eye, RotateCcw, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useUploadDocuments } from "./UploadDocumentsData";
import COMPANY_LOGO from "../../assets/logo/AcrossLogo.png";
import "./index.scss";

const FILE_PREVIEW_LIMIT = 6;

const getFileExtension = (fileName) => fileName?.split('.').pop()?.toLowerCase() || 'file';

const getIconColor = (ext) => {
    if (['pdf'].includes(ext)) return 'icon-pdf';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'icon-img';
    return 'icon-default';
};

const getDocumentIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('bank')) return Building2;
    if (n.includes('address')) return MapPin;
    return FileText;
};

const DocumentCard = ({ document, uploadingStatus, uploadProgress, handleFileSelect, handleReupload }) => {
    const [showAll, setShowAll] = useState(false);

    const documentKey = (document.entityDocumentId && document.entityDocumentId !== 0)
        ? document.entityDocumentId
        : document.documentRequirementId;

    const status = uploadingStatus[documentKey];
    const progress = uploadProgress[documentKey] || 0;
    const uploadedFiles = document.files?.filter(f => f.isUploaded) || [];
    const hasFiles = uploadedFiles.length > 0;
    const Icon = getDocumentIcon(document.documentName);

    const totalFiles = uploadedFiles.length;
    const progressPct = totalFiles > 0 ? 100 : (status === "uploading" ? progress : 0);
    const visibleFiles = showAll ? uploadedFiles : uploadedFiles.slice(0, FILE_PREVIEW_LIMIT);
    const leftCol = visibleFiles.filter((_, i) => i % 2 === 0);
    const rightCol = visibleFiles.filter((_, i) => i % 2 === 1);

    return (
        <div className={`doc-card ${hasFiles ? 'doc-card--uploaded' : ''}`}>
            {/* ── Card Header ── */}
            <div className="doc-card__header">
                <div className="doc-card__title-group">
                    <div className="doc-card__icon-box">
                        <Icon size={20} />
                    </div>
                    <div>
                        <h3 className="doc-card__name">{document.documentName}</h3>
                        <p className="doc-card__sub">
                            {document.description || `Upload ${document.documentName.toLowerCase()}`}
                        </p>
                    </div>
                </div>
                <div className="doc-card__header-actions">
                    <label
                        htmlFor={`file-${documentKey}`}
                        className={`doc-card__upload-btn ${status === "uploading" ? "disabled" : ""}`}
                        title="Upload file"
                    >
                        <input
                            type="file"
                            id={`file-${documentKey}`}
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileSelect(e, document)}
                            disabled={status === "uploading"}
                            className="doc-card__file-input"
                            multiple
                        />
                        <Upload size={15} />
                    </label>
                    {/* <button className="doc-card__more-btn" title="More options">
                        <MoreHorizontal size={16} />
                    </button> */}
                </div>
            </div>

            {/* ── File info line ── */}
            <p className="doc-card__meta">
                📄 Supported: PDF, JPG, PNG &nbsp;|&nbsp; Max 5MB
            </p>

            {/* ── Progress bar ── */}
            {(hasFiles || status === "uploading") && (
                <div className="doc-card__progress-row">
                    <CheckCircle size={16} className="doc-card__progress-check" />
                    <span className="doc-card__progress-label">
                        {status === "uploading"
                            ? `Uploading… ${progress}%`
                            : `${totalFiles} / ${totalFiles} files uploaded`}
                    </span>
                    <div className="doc-card__progress-bar">
                        <div className="doc-card__progress-fill" style={{ width: `${progressPct}%` }} />
                    </div>
                    {/* <button className="doc-card__more-btn">
                        <MoreHorizontal size={15} />
                    </button> */}
                </div>
            )}

            {/* ── Uploaded files section ── */}
            {hasFiles && (
                <>
                    <div className="doc-card__files-header">
                        <span className="doc-card__files-title">
                            Uploaded Files ({totalFiles})
                            <ChevronDown size={14} />
                        </span>
                        {/* <div className="doc-card__files-actions">
                            <button className="doc-card__action-btn">
                                Easily <ChevronDown size={13} />
                            </button>
                            <button className="doc-card__action-btn">
                                <Download size={13} /> Download <ChevronDown size={13} />
                            </button>
                            <button className="doc-card__action-btn doc-card__action-btn--delete">
                                <Trash2 size={13} /> Delete
                            </button>
                            <button className="doc-card__more-btn">
                                <MoreHorizontal size={15} />
                            </button>
                        </div> */}
                    </div>

                    {/* 2-column file grid */}
                    <div className="doc-card__files-grid">
                        <div className="doc-card__files-col">
                            {leftCol.map((file, i) => (
                                <FileRow key={i} file={file} document={document} handleReupload={handleReupload} />
                            ))}
                        </div>
                        <div className="doc-card__files-col">
                            {rightCol.map((file, i) => (
                                <FileRow key={i} file={file} document={document} handleReupload={handleReupload} />
                            ))}
                        </div>
                    </div>

                    {totalFiles > FILE_PREVIEW_LIMIT && (
                        <button
                            className="doc-card__show-toggle"
                            onClick={() => setShowAll(p => !p)}
                        >
                            {showAll
                                ? <><ChevronUp size={14} /> Show less</>
                                : <><ChevronDown size={14} /> Show more</>}
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

const FileRow = ({ file, document, handleReupload }) => {
    const ext = getFileExtension(file.fileName);
    const reuploadId = `reupload-${file.entityDocumentId}`;
    return (
        <div className="file-row">
            <div className="file-row__left">
                <span className={`file-row__icon ${getIconColor(ext)}`}>
                    <FileText size={14} />
                </span>
                <span className="file-row__name" title={file.fileName}>{file.fileName}</span>
            </div>
            <div className="file-row__right">
                {file.fileSize && <span className="file-row__size">{file.fileSize}</span>}
                <CheckCircle size={13} className="file-row__check" />
                <span className="file-row__status">Uploaded</span>
                <a href={file.filePath} target="_blank" rel="noopener noreferrer" className="file-row__btn" title="View">
                    <Eye size={13} />
                </a>
                <label htmlFor={reuploadId} className="file-row__btn" title="Re-upload" style={{ cursor: "pointer" }}>
                    <input
                        type="file"
                        id={reuploadId}
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={(e) => handleReupload(e, file, document)}
                    />
                    <RotateCcw size={13} />
                </label>
            </div>
        </div>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────

const UploadDocuments = () => {
    const [searchParams] = useSearchParams();

    const {
        errors,
        uploadingStatus,
        uploadProgress,
        requiredDocumentsField,
        isLoadingDocuments,
        handleFileSelect,
        handleReupload,
    } = useUploadDocuments(searchParams);

    if (isLoadingDocuments) {
        return (
            <div className="upload-page">
                <div className="upload-page__logo">
                    <img src={COMPANY_LOGO} alt="Logo" />
                </div>
                <div className="upload-page__loading">
                    <div className="spinner" />
                    <p>Loading required documents…</p>
                </div>
            </div>
        );
    }

    if (errors.fetch) {
        return (
            <div className="upload-page">
                <div className="upload-page__logo">
                    <img src={COMPANY_LOGO} alt="Logo" />
                </div>
                <div className="upload-page__error">
                    <p>{errors.fetch}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-page">
            <div className="upload-page__logo">
                <img src={COMPANY_LOGO} alt="Logo" />
            </div>

            <div className="upload-page__list">
                {requiredDocumentsField.map((document) => {
                    const key = (document.entityDocumentId && document.entityDocumentId !== 0)
                        ? document.entityDocumentId
                        : document.documentRequirementId;
                    return (
                        <DocumentCard
                            key={key}
                            document={document}
                            uploadingStatus={uploadingStatus}
                            uploadProgress={uploadProgress}
                            handleFileSelect={handleFileSelect}
                            handleReupload={handleReupload}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default UploadDocuments;
